import {
  ChangeEvent,
  FC,
  MouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Formik } from 'formik';
import moment from 'moment';
import styled from 'styled-components';
import colors from 'constants/colors';

import TagPicker, { toggleTag } from 'components/Ponder/TagPicker';
import { NotesInput } from 'components/Ponder/styles';
import { requestUpdateExternalWorkout } from 'actions/activities';
import { hideModal } from 'actions/modal';
import useSessionTags from 'hooks/useSessionTags';
import { Activity } from 'reducers/activities/types';
import { StoreType } from 'reducers/types';
import imageUrlGenerator from 'utils/imageUrlGenerator';
import { Content, Background as Overlay } from '../styles';
import {
  EASE_ORDER,
  EASE_RATING,
  PhotoPicker,
  WorkoutTypeInput,
  toCompletedAt,
  toDateInputValue,
  usePhotoPicker,
} from '../LogExternalWorkout/shared';
import {
  AddPhoto,
  Body,
  CloseButton,
  ErrorText,
  Eyebrow,
  FeltCard,
  FeltDescription,
  FeltName,
  FeltOption,
  FeltOptions,
  Footer,
  Header,
  HeaderTop,
  HiddenFileInput,
  MinutesRow,
  Muted,
  NotesBlock,
  PencilIcon,
  Photo,
  PhotoAction,
  PhotoActions,
  PhotoFrame,
  PrimaryButton,
  SecondaryButton,
  Section,
  SectionLabel,
  Sheet,
  StatGrid,
  StatInput,
  StatLabel,
  StatSubValue,
  StatTile,
  StatValue,
  Tag,
  TagList,
  Title,
  TitleField,
  notesBox,
} from './styles';

type Props = {
  modalData: {
    workoutId: number;
  };
};

type FormValues = {
  workoutType: string;
  completedAt: string;
  timeLength: string;
  tagIds: number[];
  rating: number;
  ponder: string;
};

// Centres the sheet vertically on desktop; on mobile it fills the screen
// Same box as the read-only notes, so the text stays put when editing starts
const NotesField = styled(NotesInput)`
  ${notesBox}

  &:focus {
    border-color: ${colors.main.blue};
    outline: none;
  }
`;

const Positioner = styled.div`
  display: flex;
  align-items: center;
  min-height: 100vh;

  @media (max-width: 992px) {
    height: 100%;
  }
`;

const photoUrl = (picture?: string | null) =>
  picture ? imageUrlGenerator(picture, '1200x480', '800x400') : null;

const feltOption = (rating?: number) => EASE_RATING.options.find(({ value }) => value === rating);

const Pencil = () => (
  <PencilIcon viewBox="0 0 20 20" aria-hidden="true">
    <path d="M18.8 1.2A3.8 3.8 0 0 0 16 0a3.6 3.6 0 0 0-2.8 1.2L1.7 12.6a2.2 2.2 0 0 0-.3.5L0 18.8a.8.8 0 0 0 1.2 1.2l5.7-1.4c.2 0 .3-.1.5-.3L18.8 6.9A4 4 0 0 0 20 4a3.6 3.6 0 0 0-1.2-2.8ZM3.2 14.5l2 .4.4 2-3.2.8Zm10.6-11.2.7-.7a2 2 0 0 1 2.9 2.9l-.7.7Z" />
  </PencilIcon>
);

const PhotoEditor = ({ photo }: { photo: PhotoPicker }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const choose = () => fileInputRef.current?.click();

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.currentTarget.files?.[0];
    if (file) photo.pick(file);
  };

  const handleRemove = () => {
    photo.remove();
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  return (
    <>
      {photo.preview ? (
        <PhotoFrame>
          <Photo src={photo.preview} alt="Workout photo" />
          <PhotoActions>
            <PhotoAction type="button" onClick={choose}>
              Change photo
            </PhotoAction>
            <PhotoAction type="button" onClick={handleRemove}>
              Remove photo
            </PhotoAction>
          </PhotoActions>
        </PhotoFrame>
      ) : (
        <AddPhoto type="button" onClick={choose}>
          + Add a photo
        </AddPhoto>
      )}
      <HiddenFileInput
        ref={fileInputRef}
        type="file"
        accept="image/*"
        aria-label="Workout photo"
        onChange={handleChange}
      />
    </>
  );
};

type CardProps = {
  workout: Activity;
  isEditing: boolean;
  onEdit: () => void;
  onCancel: () => void;
  onClose: (event: MouseEvent) => void;
};

/**
 * The details and the edit form share one layout: the same header, sections in
 * the same order, and the same footer, so editing reads as the card becoming
 * editable rather than a different screen.
 */
const WorkoutCard = ({ workout, isEditing, onEdit, onCancel, onClose }: CardProps) => {
  const dispatch = useDispatch();
  const isSubmitting = useSelector((state: StoreType) => state.activities.isUpdatingExternal);
  const submitError = useSelector((state: StoreType) => state.activities.updateExternalError);
  const [hasSubmitted, setHasSubmitted] = useState(false);

  const savedPhoto = photoUrl(workout.picture);
  const photo = usePhotoPicker(savedPhoto);
  const sessionTags = useSessionTags();

  // Tags retired since this workout was logged aren't in the vocabulary any
  // more, but stay on offer so they can be kept or removed.
  const tagOptions = useMemo(() => {
    const vocabulary = sessionTags ?? [];
    const known = new Set(vocabulary.map(({ id }) => id));
    return [...vocabulary, ...(workout.tags ?? []).filter(({ id }) => !known.has(id))];
  }, [sessionTags, workout.tags]);

  const initialDate = useMemo(() => {
    const date = new Date(workout.completedAt ?? Date.now());
    return toDateInputValue(Number.isNaN(date.getTime()) ? new Date() : date);
  }, [workout.completedAt]);

  const initialValues: FormValues = {
    workoutType: workout.workoutType ?? '',
    completedAt: initialDate,
    timeLength: workout.timeLength ? `${workout.timeLength}` : '',
    tagIds: (workout.tags ?? []).map(({ id }) => id),
    rating: workout.rating ?? 0,
    ponder: workout.ponder ?? '',
  };

  const onSubmit = (values: FormValues) => {
    setHasSubmitted(true);
    dispatch(
      requestUpdateExternalWorkout({
        id: workout.id,
        workoutType: values.workoutType.trim(),
        timeLength: parseInt(values.timeLength, 10),
        tagIds: values.tagIds,
        rating: values.rating,
        ponder: values.ponder.trim(),
        ...(values.completedAt !== initialDate
          ? { completedAt: toCompletedAt(values.completedAt) }
          : {}),
        ...(photo.formData ? { file: photo.formData } : {}),
        ...(photo.isRemoved && !photo.formData ? { removePicture: true } : {}),
      }),
    );
  };

  const date = moment(workout.completedAt);
  const felt = feltOption(workout.rating);
  const today = toDateInputValue(new Date());

  return (
    <Formik initialValues={initialValues} onSubmit={onSubmit}>
      {({ values, handleChange, handleBlur, handleSubmit, setFieldValue }) => {
        const minutes = parseInt(values.timeLength, 10);
        const canSave =
          !!values.workoutType.trim() &&
          !!values.completedAt &&
          minutes > 0 &&
          minutes <= 1440 &&
          !!values.rating;
        const pickedFelt = feltOption(values.rating);

        return (
          <Sheet role="dialog" aria-modal="true" aria-labelledby="external-workout-title">
            <Header>
              <HeaderTop>
                <Eyebrow>External workout</Eyebrow>
                {isEditing && <Eyebrow $active>Editing</Eyebrow>}
                <CloseButton type="button" aria-label="Close" onClick={onClose} />
              </HeaderTop>
              {isEditing ? (
                <TitleField id="external-workout-title">
                  <WorkoutTypeInput
                    name="workoutType"
                    value={values.workoutType}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    onSelect={(label) => setFieldValue('workoutType', label)}
                  />
                </TitleField>
              ) : (
                <Title id="external-workout-title">{workout.workoutType}</Title>
              )}
            </Header>

            <Body key={isEditing ? 'edit' : 'view'}>
              {/* A photo sits on top in both modes; adding one is offered at the end */}
              {isEditing
                ? (savedPhoto || photo.formData) && <PhotoEditor photo={photo} />
                : savedPhoto && (
                    <PhotoFrame>
                      <Photo src={savedPhoto} alt={`Photo from ${workout.workoutType}`} />
                    </PhotoFrame>
                  )}

              <StatGrid>
                <StatTile as={isEditing ? 'label' : 'div'}>
                  <StatLabel>Date</StatLabel>
                  {isEditing ? (
                    <StatInput
                      name="completedAt"
                      aria-label="Date"
                      type="date"
                      value={values.completedAt}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      max={today}
                    />
                  ) : (
                    <StatValue>
                      {date.isValid() ? date.format('ddd, D MMM') : '—'}
                      {date.isValid() && <StatSubValue>{date.format('YYYY')}</StatSubValue>}
                    </StatValue>
                  )}
                </StatTile>
                <StatTile as={isEditing ? 'label' : 'div'}>
                  <StatLabel>Duration</StatLabel>
                  {isEditing ? (
                    <MinutesRow>
                      <input
                        name="timeLength"
                        aria-label="Minutes"
                        type="number"
                        inputMode="numeric"
                        value={values.timeLength}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        placeholder="30"
                        style={{ width: `${Math.max(values.timeLength.length, 2) + 0.5}ch` }}
                        min={1}
                        max={1440}
                      />
                      <StatSubValue>min</StatSubValue>
                    </MinutesRow>
                  ) : (
                    <StatValue>
                      {workout.timeLength ?? '—'}
                      <StatSubValue>min</StatSubValue>
                    </StatValue>
                  )}
                </StatTile>
              </StatGrid>

              <Section>
                <SectionLabel>How it felt</SectionLabel>
                {isEditing ? (
                  <>
                    <FeltOptions role="group" aria-label={EASE_RATING.label}>
                      {EASE_ORDER.map((value) => (
                        <FeltOption
                          key={value}
                          type="button"
                          aria-pressed={values.rating === value}
                          onClick={() => setFieldValue('rating', value)}
                        >
                          {feltOption(value)?.name}
                        </FeltOption>
                      ))}
                    </FeltOptions>
                    <FeltDescription>
                      {pickedFelt?.description ?? 'Pick the one closest to how your body felt.'}
                    </FeltDescription>
                  </>
                ) : felt ? (
                  <FeltCard>
                    <FeltName>{felt.name}</FeltName>
                    <FeltDescription>{felt.description}</FeltDescription>
                  </FeltCard>
                ) : (
                  <Muted>Not rated</Muted>
                )}
              </Section>

              <Section>
                <SectionLabel>Tags</SectionLabel>
                {isEditing ? (
                  tagOptions.length > 0 ? (
                    <TagPicker
                      tags={tagOptions}
                      selectedIds={values.tagIds}
                      onToggle={(tagId) => setFieldValue('tagIds', toggleTag(values.tagIds, tagId))}
                    />
                  ) : (
                    <Muted>Loading tags…</Muted>
                  )
                ) : workout.tags?.length ? (
                  <TagList aria-label="Tags">
                    {workout.tags.map(({ id, label }) => (
                      <Tag key={id}>{label}</Tag>
                    ))}
                  </TagList>
                ) : (
                  <Muted>No tags</Muted>
                )}
              </Section>

              <Section>
                <SectionLabel>Notes</SectionLabel>
                {isEditing ? (
                  <NotesField
                    name="ponder"
                    aria-label="Notes"
                    minRows={3}
                    value={values.ponder}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder="Anything worth remembering."
                    maxLength={5000}
                  />
                ) : workout.ponder ? (
                  <NotesBlock>{workout.ponder}</NotesBlock>
                ) : (
                  <Muted>No notes</Muted>
                )}
              </Section>

              {isEditing && !savedPhoto && !photo.formData && (
                <Section>
                  <SectionLabel>Photo</SectionLabel>
                  <PhotoEditor photo={photo} />
                </Section>
              )}

              {isEditing && hasSubmitted && submitError && (
                <ErrorText role="alert">{submitError}</ErrorText>
              )}
            </Body>

            <Footer>
              {isEditing ? (
                <>
                  <SecondaryButton type="button" onClick={onCancel} disabled={isSubmitting}>
                    Cancel
                  </SecondaryButton>
                  <PrimaryButton
                    type="button"
                    onClick={() => handleSubmit()}
                    disabled={!canSave || isSubmitting}
                  >
                    {isSubmitting ? 'Saving…' : 'Save changes'}
                  </PrimaryButton>
                </>
              ) : (
                <>
                  <SecondaryButton type="button" onClick={onClose}>
                    Done
                  </SecondaryButton>
                  <PrimaryButton type="button" onClick={onEdit}>
                    <Pencil />
                    Edit
                  </PrimaryButton>
                </>
              )}
            </Footer>
          </Sheet>
        );
      }}
    </Formik>
  );
};

const ExternalWorkoutDetails: FC<Props> = ({ modalData }) => {
  const dispatch = useDispatch();
  const workoutId = modalData?.workoutId;
  const workout = useSelector((state: StoreType) =>
    (state.activities.all as Activity[]).find(
      ({ id, type }) => type === 'external' && id === workoutId,
    ),
  );
  const isUpdating = useSelector((state: StoreType) => state.activities.isUpdatingExternal);
  const updateError = useSelector((state: StoreType) => state.activities.updateExternalError);
  const [isEditing, setIsEditing] = useState(false);
  // Remounting the card on leaving edit mode drops unsaved form and photo state
  const [formKey, setFormKey] = useState(0);

  const leaveEditing = useCallback(() => {
    setIsEditing(false);
    setFormKey((key) => key + 1);
  }, []);

  // Back to the details once a save goes through
  const wasUpdating = useRef(isUpdating);
  useEffect(() => {
    if (wasUpdating.current && !isUpdating && !updateError) {
      leaveEditing();
    }
    wasUpdating.current = isUpdating;
  }, [isUpdating, updateError, leaveEditing]);

  const handleClose = useCallback(
    (e: MouseEvent) => {
      e.preventDefault();
      dispatch(hideModal());
    },
    [dispatch],
  );

  return (
    <>
      <Overlay onClick={handleClose} />
      <Content>
        <Positioner>
          {workout ? (
            <WorkoutCard
              key={formKey}
              workout={workout}
              isEditing={isEditing}
              onEdit={() => setIsEditing(true)}
              onCancel={leaveEditing}
              onClose={handleClose}
            />
          ) : (
            <Sheet role="dialog" aria-modal="true" aria-label="Workout">
              <Header>
                <HeaderTop>
                  <Eyebrow>External workout</Eyebrow>
                  <CloseButton type="button" aria-label="Close" onClick={handleClose} />
                </HeaderTop>
              </Header>
              <Body>
                <Muted>This workout is no longer available.</Muted>
              </Body>
            </Sheet>
          )}
        </Positioner>
      </Content>
    </>
  );
};

export default ExternalWorkoutDetails;
